import React, { ChangeEvent, useEffect, useState } from 'react';
import Button from 'components/button';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import Header from 'components/text/header';
import UseApi from 'hooks/useApi';
import { useParams } from 'react-router-dom';
import ImageUpload from 'components/imageUpload';

interface ItineraryItem {
  time: string;
  activity: string;
}

interface ItineraryDay {
  day: number;
  items: ItineraryItem[];
}

interface PackageFormState {
  packageName: string;
  startDate: string;
  endDate: string;
  totalSeats: string;
  availableSeats: string;
  price_double_sharing: string;
  price_triple_sharing: string;
  price_quad_sharing: string;
  overview: string;
  tripType: string;
  itinerary: ItineraryDay[];
  additionalFacilities: string[];
  pictures: string[];
  mainImage: string;
  endUserReferralDiscount: string;
  referrerCommission: string;
}

interface ImageResponse {
  message: string;
  data: string;
}

const initialValues: PackageFormState = {
  packageName: '',
  startDate: '',
  endDate: '',
  totalSeats: '',
  availableSeats: '',
  price_double_sharing: '',
  price_triple_sharing: '',
  price_quad_sharing: '',
  overview: '',
  tripType: 'Domestic',
  itinerary: [{ day: 1, items: [{ time: '', activity: '' }] }],
  additionalFacilities: [] as string[],
  pictures: [] as string[],
  mainImage: '',
  endUserReferralDiscount: '0',
  referrerCommission: '0',
};

interface IProps {
  formValues?: PackageFormState;
  type: 'Create' | 'Update';
  onSubmit?: () => void;
  handleToggleCreatePackage?: () => void;
}

const PackageForm: React.FC<IProps> = ({ formValues, type, onSubmit, handleToggleCreatePackage}) => {
  const { patchApi, postApi } = UseApi();
  const { id } = useParams<{ id: string }>();
  const [values, setValues] = useState<PackageFormState>(
    formValues || initialValues,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleMainImageChange = (e: any) => {
    const { name, value } = e.target;
    
    if (value && typeof value === 'object' && 
        'message' in value && value.message === 'Success' && 
        'data' in value) {
      setValues({
        ...values,
        [name]: value.data,
      });
    } else {
      setValues({
        ...values,
        [name]: value,
      });
    }
    
    if (errors.mainImage) {
      setErrors({
        ...errors,
        mainImage: '',
      });
    }
  };

  const handlePictureChange = (index: number, e: any) => {
    const updatedPictures = [...values.pictures];
    
    if (e.target && e.target.value) {
      if (typeof e.target.value === 'object' && 
          e.target.value && 
          'message' in e.target.value && 
          e.target.value.message === 'Success' && 
          'data' in e.target.value) {
        updatedPictures[index] = (e.target.value as any).data;
      } else {
        updatedPictures[index] = e.target.value;
      }
    }

    setValues({
      ...values,
      pictures: updatedPictures,
    });
  };

  const handleItineraryChange = (
    dayIndex: number,
    itemIndex: number,
    field: string,
    value: string | number,
  ) => {
    const updatedItinerary = [...values.itinerary];
    if (field === 'day') {
      updatedItinerary[dayIndex].day = Number(value);
    } else {
      updatedItinerary[dayIndex].items[itemIndex][
        field as keyof ItineraryItem
      ] = value as string;
    }

    setValues({
      ...values,
      itinerary: updatedItinerary,
    });
  };

  const addItineraryDay = () => {
    const newDay: ItineraryDay = {
      day: values.itinerary.length + 1,
      items: [{ time: '', activity: '' }],
    };

    setValues({
      ...values,
      itinerary: [...values.itinerary, newDay],
    });
  };

  const removeItineraryDay = (dayIndex: number) => {
    if (values.itinerary.length > 1) {
      const updatedItinerary = values.itinerary.filter((_, index) => index !== dayIndex);
      
      const reorderedItinerary = updatedItinerary.map((day, index) => ({
        ...day,
        day: index + 1,
      }));
      
      setValues({
        ...values,
        itinerary: reorderedItinerary,
      });
    }
  };

  const addItineraryItem = (dayIndex: number) => {
    const updatedItinerary = [...values.itinerary];
    updatedItinerary[dayIndex].items.push({ time: '', activity: '' });

    setValues({
      ...values,
      itinerary: updatedItinerary,
    });
  };

  const removeItineraryItem = (dayIndex: number, itemIndex: number) => {
    if (values.itinerary[dayIndex].items.length > 1) {
      const updatedItinerary = [...values.itinerary];
      updatedItinerary[dayIndex].items = updatedItinerary[dayIndex].items.filter(
        (_, index) => index !== itemIndex
      );

      setValues({
        ...values,
        itinerary: updatedItinerary,
      });
    }
  };

  const handleFacilityChange = (index: number, value: string) => {
    const updatedFacilities = [...values.additionalFacilities];
    updatedFacilities[index] = value;

    setValues({
      ...values,
      additionalFacilities: updatedFacilities,
    });
  };

  const addFacility = () => {
    setValues({
      ...values,
      additionalFacilities: [...values.additionalFacilities, ''],
    });
  };

  const removeFacility = (index: number) => {
    const updatedFacilities = values.additionalFacilities.filter(
      (_, i) => i !== index
    );

    setValues({
      ...values,
      additionalFacilities: updatedFacilities,
    });
  };
  
  const addPicture = () => {
    setValues({
      ...values,
      pictures: [...values.pictures, ''],
    });
  };

  const removePicture = (index: number) => {
    const updatedPictures = values.pictures.filter((_, i) => i !== index);

    setValues({
      ...values,
      pictures: updatedPictures,
    });
  };

  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!values.packageName.trim())
      newErrors.packageName = 'Package name is required';
    if (!values.overview.trim()) newErrors.overview = 'Overview is required';
    if (!values.startDate) newErrors.startDate = 'Start date is required';
    if (!values.endDate) newErrors.endDate = 'End date is required';
    if (!values.totalSeats) newErrors.totalSeats = 'Total seats are required';
    if (!values.availableSeats)
      newErrors.availableSeats = 'Available seats are required';
    if (!values.price_double_sharing)
      newErrors.price_double_sharing = 'Double sharing price is required';
    if (!values.price_triple_sharing)
      newErrors.price_triple_sharing = 'Triple sharing price is required';
    if (!values.price_quad_sharing)
      newErrors.price_quad_sharing = 'Quad sharing price is required';
    if (!values.mainImage) newErrors.mainImage = 'Main image is required';

    const hasEmptyItineraryItems = values.itinerary.some((day) =>
      day.items.some((item) => !item.time.trim() || !item.activity.trim()),
    );

    if (hasEmptyItineraryItems) {
      newErrors.itinerary = 'All itinerary items must have time and activity';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      let res;
      
      const submissionData = {
        ...values,
        mainImage: typeof values.mainImage === 'object' && values.mainImage && 'data' in (values.mainImage as any)
          ? (values.mainImage as any).data 
          : values.mainImage,
        pictures: values.pictures.map(pic => 
          typeof pic === 'object' && pic && 'data' in (pic as any) ? (pic as any).data : pic
        ).filter(pic => pic)
      };
      
      if (type === 'Update' && id) {
        res = await patchApi(`packages/${id}`, submissionData);
      } else {
        res = await postApi(`packages`, submissionData);
      }

      if (res?.data.message) {
        resetForm();
        onSubmit?.();
        handleToggleCreatePackage?.();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (formValues) {
      const formattedValues = {
        ...formValues,
        startDate: formValues.startDate
          ? formValues.startDate.substring(0, 10)
          : '',
        endDate: formValues.endDate ? formValues.endDate.substring(0, 10) : '',
      };
      setValues(formattedValues);
    }
  }, [formValues]);

  return (
    <Card extra="p-4 mb-4">
      <Header>{type} Package</Header>
      <InputField
        variant="auth"
        extra="mb-3"
        label="Package Name*"
        placeholder="Enter package name"
        id="packageName"
        type="text"
        value={values.packageName}
        onChange={handleInputChange}
        name="packageName"
        state={errors.packageName ? 'error' : ''}
      />
      <div className="mb-3">
        <label
          htmlFor="overview"
          className={`text-sm font-medium ${
            errors.overview ? 'text-red-500' : 'text-gray-900'
          }`}
        >
          Overview*
        </label>
        <textarea
          className={`mt-2 flex h-32 w-full items-center justify-center rounded-xl border bg-white p-3 text-sm outline-none ${
            errors.overview ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter package overview"
          id="overview"
          value={values.overview}
          onChange={handleInputChange}
          name="overview"
        />
        {errors.overview && (
          <p className="mt-1 text-xs text-red-500">{errors.overview}</p>
        )}
      </div>
      <div className="mb-3">
        <label htmlFor="tripType" className="text-sm font-medium text-gray-900">
          Trip Type*
        </label>
        <select
          className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white p-3 text-sm outline-none"
          id="tripType"
          value={values.tripType}
          onChange={handleInputChange}
          name="tripType"
        >
          <option value="Domestic">Domestic</option>
          <option value="International">International</option>
        </select>
      </div>
      <InputField
        variant="auth"
        extra="mb-3"
        label="Start Date*"
        placeholder="Enter start date"
        id="startDate"
        type="date"
        value={values.startDate}
        onChange={handleInputChange}
        name="startDate"
        state={errors.startDate ? 'error' : ''}
      />
      <InputField
        variant="auth"
        extra="mb-3"
        label="End Date*"
        placeholder="Enter end date"
        id="endDate"
        type="date"
        value={values.endDate}
        onChange={handleInputChange}
        name="endDate"
        state={errors.endDate ? 'error' : ''}
      />
      <InputField
        variant="auth"
        extra="mb-3"
        label="Total Seats*"
        placeholder="Enter total seats"
        id="totalSeats"
        type="number"
        value={values.totalSeats}
        onChange={handleInputChange}
        name="totalSeats"
        state={errors.totalSeats ? 'error' : ''}
      />
      <InputField
        variant="auth"
        extra="mb-3"
        label="Available Seats*"
        placeholder="Enter available seats"
        id="availableSeats"
        type="number"
        value={values.availableSeats}
        onChange={handleInputChange}
        name="availableSeats"
        state={errors.availableSeats ? 'error' : ''}
      />
      <InputField
        variant="auth"
        extra="mb-3"
        label="Price Double Sharing*"
        placeholder="Enter price for double sharing"
        id="price_double_sharing"
        type="number"
        value={values.price_double_sharing}
        onChange={handleInputChange}
        name="price_double_sharing"
        state={errors.price_double_sharing ? 'error' : ''}
      />
      <InputField
        variant="auth"
        extra="mb-3"
        label="Price Triple Sharing*"
        placeholder="Enter price for triple sharing"
        id="price_triple_sharing"
        type="number"
        value={values.price_triple_sharing}
        onChange={handleInputChange}
        name="price_triple_sharing"
        state={errors.price_triple_sharing ? 'error' : ''}
      />
      <InputField
        variant="auth"
        extra="mb-3"
        label="Price Quad Sharing*"
        placeholder="Enter price for quad sharing"
        id="price_quad_sharing"
        type="number"
        value={values.price_quad_sharing}
        onChange={handleInputChange}
        name="price_quad_sharing"
        state={errors.price_quad_sharing ? 'error' : ''}
      />
      <InputField
        variant="auth"
        extra="mb-3"
        label="Referral Discount (PKR)"
        placeholder="Enter discount amount for referred customers"
        id="endUserReferralDiscount"
        type="number"
        value={values.endUserReferralDiscount}
        onChange={handleInputChange}
        name="endUserReferralDiscount"
        state={errors.endUserReferralDiscount ? 'error' : ''}
      />
      <InputField
        variant="auth"
        extra="mb-3"
        label="Referrer Commission (PKR)"
        placeholder="Enter commission amount for referrers"
        id="referrerCommission"
        type="number"
        value={values.referrerCommission}
        onChange={handleInputChange}
        name="referrerCommission"
        state={errors.referrerCommission ? 'error' : ''}
      />
      
      <ImageUpload
        id="mainImage"
        name="mainImage"
        label="Main Package Image*"
        value={values.mainImage}
        onChange={handleMainImageChange}
        error={errors.mainImage || null}
        extra="mb-3"
        folder="packages"
      />
      
      <div className="mb-3">
        <label
          className={`text-sm font-medium ${
            errors.itinerary ? 'text-red-500' : 'text-gray-900'
          }`}
        >
          Itinerary*
        </label>
        {errors.itinerary && (
          <p className="mt-1 text-xs text-red-500">{errors.itinerary}</p>
        )}

        {values.itinerary.map((day, dayIndex) => (
          <div key={dayIndex} className="mt-3 rounded-xl border p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex-grow">
                <label className="text-sm font-medium text-gray-900">Day</label>
                <InputField
                  variant="auth"
                  extra="mb-2"
                  placeholder="Day number"
                  type="number"
                  value={day.day}
                  onChange={(e) =>
                    handleItineraryChange(dayIndex, 0, 'day', e.target.value)
                  }
                  id={`day-${dayIndex}`}
                  name={`day-${dayIndex}`}
                  label=""
                />
              </div>
              {dayIndex > 0 && (
                <button
                  type="button"
                  onClick={() => removeItineraryDay(dayIndex)}
                  className="ml-2 rounded-md bg-red-500 px-2 py-1 text-sm text-white hover:bg-red-600"
                >
                  Remove Day
                </button>
              )}
            </div>

            {day.items.map((item, itemIndex) => (
              <div key={itemIndex} className="mb-2 ml-4 border-l p-2">
                <div className="flex items-center justify-between">
                  <div className="flex-grow">
                    <div className="mb-2">
                      <label className="text-sm font-medium text-gray-900">Time</label>
                      <select
                        className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white p-3 text-sm outline-none"
                        value={item.time}
                        onChange={(e) =>
                          handleItineraryChange(
                            dayIndex,
                            itemIndex,
                            'time',
                            e.target.value,
                          )
                        }
                        id={`time-${dayIndex}-${itemIndex}`}
                        name={`time-${dayIndex}-${itemIndex}`}
                      >
                        <option value="">Select time</option>
                        <option value="12:00 AM">12:00 AM</option>
                        <option value="12:30 AM">12:30 AM</option>
                        <option value="1:00 AM">1:00 AM</option>
                        <option value="1:30 AM">1:30 AM</option>
                        <option value="2:00 AM">2:00 AM</option>
                        <option value="2:30 AM">2:30 AM</option>
                        <option value="3:00 AM">3:00 AM</option>
                        <option value="3:30 AM">3:30 AM</option>
                        <option value="4:00 AM">4:00 AM</option>
                        <option value="4:30 AM">4:30 AM</option>
                        <option value="5:00 AM">5:00 AM</option>
                        <option value="5:30 AM">5:30 AM</option>
                        <option value="6:00 AM">6:00 AM</option>
                        <option value="6:30 AM">6:30 AM</option>
                        <option value="7:00 AM">7:00 AM</option>
                        <option value="7:30 AM">7:30 AM</option>
                        <option value="8:00 AM">8:00 AM</option>
                        <option value="8:30 AM">8:30 AM</option>
                        <option value="9:00 AM">9:00 AM</option>
                        <option value="9:30 AM">9:30 AM</option>
                        <option value="10:00 AM">10:00 AM</option>
                        <option value="10:30 AM">10:30 AM</option>
                        <option value="11:00 AM">11:00 AM</option>
                        <option value="11:30 AM">11:30 AM</option>
                        <option value="12:00 PM">12:00 PM</option>
                        <option value="12:30 PM">12:30 PM</option>
                        <option value="1:00 PM">1:00 PM</option>
                        <option value="1:30 PM">1:30 PM</option>
                        <option value="2:00 PM">2:00 PM</option>
                        <option value="2:30 PM">2:30 PM</option>
                        <option value="3:00 PM">3:00 PM</option>
                        <option value="3:30 PM">3:30 PM</option>
                        <option value="4:00 PM">4:00 PM</option>
                        <option value="4:30 PM">4:30 PM</option>
                        <option value="5:00 PM">5:00 PM</option>
                        <option value="5:30 PM">5:30 PM</option>
                        <option value="6:00 PM">6:00 PM</option>
                        <option value="6:30 PM">6:30 PM</option>
                        <option value="7:00 PM">7:00 PM</option>
                        <option value="7:30 PM">7:30 PM</option>
                        <option value="8:00 PM">8:00 PM</option>
                        <option value="8:30 PM">8:30 PM</option>
                        <option value="9:00 PM">9:00 PM</option>
                        <option value="9:30 PM">9:30 PM</option>
                        <option value="10:00 PM">10:00 PM</option>
                        <option value="10:30 PM">10:30 PM</option>
                        <option value="11:00 PM">11:00 PM</option>
                        <option value="11:30 PM">11:30 PM</option>
                      </select>
                    </div>
                    <InputField
                      variant="auth"
                      extra="mb-2"
                      label="Activity"
                      placeholder="Enter activity"
                      type="text"
                      value={item.activity}
                      onChange={(e) =>
                        handleItineraryChange(
                          dayIndex,
                          itemIndex,
                          'activity',
                          e.target.value,
                        )
                      }
                      id={`activity-${dayIndex}-${itemIndex}`}
                      name={`activity-${dayIndex}-${itemIndex}`}
                    />
                  </div>
                  {itemIndex > 0 && (
                    <button
                      type="button"
                      onClick={() => removeItineraryItem(dayIndex, itemIndex)}
                      className="ml-2 h-8 rounded-md bg-red-500 px-2 text-sm text-white hover:bg-red-600"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}

            <Button
              extraClasses="mt-2 text-sm"
              onClick={() => addItineraryItem(dayIndex)}
            >
              Add Activity
            </Button>
          </div>
        ))}

        <Button extraClasses="mt-3 text-sm" onClick={addItineraryDay}>
          Add Day
        </Button>
      </div>
      
      <div className="mb-3">
        <label className="text-sm font-medium text-gray-900">
          Additional Facilities
        </label>
        {values.additionalFacilities.map((facility, index) => (
          <div key={index} className="mb-2 flex items-center">
            <div className="flex-grow">
              <InputField
                variant="auth"
                extra="mb-0"
                placeholder="Enter facility"
                type="text"
                value={facility}
                onChange={(e) => handleFacilityChange(index, e.target.value)}
                id={`facility-${index}`}
                name={`facility-${index}`}
                label=""
              />
            </div>
            {index > 0 && (
              <button
                type="button"
                onClick={() => removeFacility(index)}
                className="ml-2 h-10 rounded-md bg-red-500 px-2 text-sm text-white hover:bg-red-600"
              >
                Remove
              </button>
            )}
          </div>
        ))}

        <Button extraClasses="mt-2 text-sm" onClick={addFacility}>
          Add Facility
        </Button>
      </div>
      
      <div className="mb-3">
        <label className="text-sm font-medium text-gray-900">
          Additional Pictures
        </label>
        
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {values.pictures.map((picture, index) => (
            <div key={index} className="relative">
              <ImageUpload
                id={`picture-${index}`}
                name={`picture-${index}`}
                label={`Picture ${index + 1}`}
                value={picture}
                onChange={(e) => handlePictureChange(index, e)}
                error={null}
                extra="mb-2"
                folder="packages"
              />
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removePicture(index)}
                  className="absolute top-0 right-0 rounded-md bg-red-500 px-2 py-1 text-sm text-white hover:bg-red-600"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        <Button extraClasses="mt-2 text-sm" onClick={addPicture}>
          Add Picture
        </Button>
      </div>

      <Button
        extraClasses={`mx-auto ${
          isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
        }`}
        onClick={isSubmitting ? undefined : handleSubmit}
      >
        {isSubmitting ? 'Processing...' : type}
      </Button>
    </Card>
  );
};

export default PackageForm;